import React, { useState } from 'react';
import { X, Check, Crown, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SubscriptionTier, authAPI } from '../services/api';
import { TIER_FEATURES } from '../constants/subscription';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useAuth } from '../contexts/AuthContext';
import './UpgradeModal.css';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  message?: string;
  onUpgradeSuccess?: () => void;
}

const UpgradeModal: React.FC<UpgradeModalProps> = ({ isOpen, onClose, message, onUpgradeSuccess }) => {
  const { tier: currentTier } = useSubscription();
  const { refreshUser } = useAuth();
  const { t } = useTranslation('common');
  const [upgrading, setUpgrading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const getTierCopy = (tier: SubscriptionTier) => {
    const key = tier === SubscriptionTier.PLATINUM ? 'platinum' : 'premium';
    const copy = t(`subscription.tiers.${key}`, { returnObjects: true }) as {
      name?: string;
      tagline?: string;
      features?: string[];
    };
    const base = TIER_FEATURES[tier];

    return {
      name: copy?.name || base.name,
      tagline: copy?.tagline || base.tagline,
      features: Array.isArray(copy?.features) && copy.features.length > 0 ? copy.features : base.features
    };
  };

  const handleUpgrade = async (targetTier: SubscriptionTier) => {
    if (targetTier === currentTier) return;

    setUpgrading(true);
    setError(null);

    try {
      // Call the upgrade API
      const campaignSource = localStorage.getItem('campaign_source') || undefined;
      const campaignSession = localStorage.getItem('tracking_session_id') || undefined;
      await authAPI.upgradeSubscription(targetTier, campaignSource, campaignSession);

      // Refresh user data to get updated subscription info
      await refreshUser();

      // Close modal and show success
      alert(t('subscription.success', { plan: getTierCopy(targetTier).name }));
      onClose();

      // Call success callback if provided
      if (onUpgradeSuccess) {
        onUpgradeSuccess();
      }

      // Reload page to apply UI changes immediately
      window.location.reload();
    } catch (err: any) {
      console.error('Upgrade error:', err);
      const errorMessage = err.response?.data?.detail || t('subscription.error');
      setError(errorMessage);
    } finally {
      setUpgrading(false);
    }
  };

  const getTierIcon = (tier: SubscriptionTier) => {
    switch (tier) {
      case SubscriptionTier.PREMIUM:
        return <Zap size={24} />;
      case SubscriptionTier.PLATINUM:
        return <Crown size={24} />;
      default:
        return null;
    }
  };

  const isCurrentTier = (tier: SubscriptionTier) => tier === currentTier;

  const renderTierGroups = (tier: SubscriptionTier) => {
    const features = TIER_FEATURES[tier];
    const copy = getTierCopy(tier);
    return (
      <div className="tier-groups">
        <div className="tier-group">
          <div className="tier-group-title">{t('subscription.includes')}</div>
          <div className="tier-lines">
            {copy.features.map((feature) => (
              <div key={feature} className="tier-line">
                <Check size={18} className="feature-check" />
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="tier-group">
          <div className="tier-group-title">{t('subscription.limits')}</div>
          <div className="tier-limits">
            <div className="tier-limit">
              {t('subscription.contractsPerMonth', { count: features.contractsPerMonth })}
            </div>
            {features.questionsPerContractMonth > 0 && (
              <div className="tier-limit">
                {t('subscription.questionsPerContractMonth', { count: features.questionsPerContractMonth })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="upgrade-modal-overlay" onClick={onClose}>
      <div className="upgrade-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="upgrade-modal-close" onClick={onClose}>
          <X size={24} />
        </button>

        <div className="upgrade-modal-header">
          <h2>{t('subscription.choosePlan')}</h2>
          {message && <p className="upgrade-message">{message}</p>}
        </div>

        <div className="upgrade-tiers-grid">
          {/* Premium Tier */}
          <div className={`upgrade-tier-card ${isCurrentTier(SubscriptionTier.PREMIUM) ? 'current' : ''}`}>
            <div className="tier-badge premium">
              {getTierIcon(SubscriptionTier.PREMIUM)}
              <span>{getTierCopy(SubscriptionTier.PREMIUM).name}</span>
            </div>

            <p className="tier-tagline">{getTierCopy(SubscriptionTier.PREMIUM).tagline}</p>

            <div className="tier-price">
              <span className="price-currency">R$</span>
              <span className="price-amount">
                {TIER_FEATURES[SubscriptionTier.PREMIUM].priceMonthly?.toFixed(2)}
              </span>
              <span className="price-period">{t('subscription.perMonth')}</span>
            </div>

            {renderTierGroups(SubscriptionTier.PREMIUM)}

            <button
              className={`tier-action-button ${isCurrentTier(SubscriptionTier.PREMIUM) ? 'current' : 'upgrade'}`}
              onClick={() => handleUpgrade(SubscriptionTier.PREMIUM)}
              disabled={isCurrentTier(SubscriptionTier.PREMIUM) || upgrading}
            >
              {upgrading
                ? t('subscription.processing')
                : isCurrentTier(SubscriptionTier.PREMIUM)
                  ? t('subscription.currentPlan')
                  : t('subscription.upgrade')}
            </button>
          </div>

          {/* Platinum Tier */}
          <div className={`upgrade-tier-card ${isCurrentTier(SubscriptionTier.PLATINUM) ? 'current' : ''} featured`}>
            <div className="tier-badge platinum">
              {getTierIcon(SubscriptionTier.PLATINUM)}
              <span>{getTierCopy(SubscriptionTier.PLATINUM).name}</span>
            </div>

            <p className="tier-tagline">{getTierCopy(SubscriptionTier.PLATINUM).tagline}</p>

            <div className="tier-price">
              <span className="price-currency">R$</span>
              <span className="price-amount">
                {TIER_FEATURES[SubscriptionTier.PLATINUM].priceMonthly?.toFixed(2)}
              </span>
              <span className="price-period">{t('subscription.perMonth')}</span>
            </div>

            {renderTierGroups(SubscriptionTier.PLATINUM)}

            <button
              className={`tier-action-button ${isCurrentTier(SubscriptionTier.PLATINUM) ? 'current' : 'upgrade'}`}
              onClick={() => handleUpgrade(SubscriptionTier.PLATINUM)}
              disabled={isCurrentTier(SubscriptionTier.PLATINUM) || upgrading}
            >
              {upgrading
                ? t('subscription.processing')
                : isCurrentTier(SubscriptionTier.PLATINUM)
                  ? t('subscription.currentPlan')
                  : t('subscription.upgrade')}
            </button>
          </div>
        </div>

        {error && (
          <div className="upgrade-error-message">
            {error}
          </div>
        )}

        <div className="upgrade-modal-footer">
          <p className="upgrade-footer-note">
            {t('subscription.footerNote')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;
